import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useState } from "react";
import Select, { components } from "react-select";
import apiClient from "../../../utils/ApiClient/apiClientAxios";

const CONFIG_TYPES = [
  "CODE",
  "MODEL",
  "DATA",
  "META",
  "SECURITY",
  "ADAPTIVE_CARD",
];

export default function ExportConfigPicker() {
  const [options, setOptions] = useState({});
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const toast = useToast();

  // === Fetch configs ===
  useEffect(() => {
    const fetchAllConfigs = async () => {
      try {
        setLoading(true);
        const results = await Promise.all(
          CONFIG_TYPES.map((type) => apiClient.get(`/listConfig?type=${type}`))
        );

        const grouped = {};
        results.forEach((res, idx) => {
          const type = CONFIG_TYPES[idx];
          const data = res.data || [];
          grouped[type] = data.map((item) => ({
            value: item.id,
            label: `${item.id}${
              item.DESCRIPTION ? ` – ${item.DESCRIPTION}` : ""
            }`,
            type,
            modifiedBy: item.MODIFIED_BY || "N/A",
            timestamp: item._ts || "",
          }));
        });

        setOptions(grouped);
        setSelected(
          CONFIG_TYPES.reduce((acc, type) => {
            acc[type] = [];
            return acc;
          }, {})
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllConfigs();
  }, [toast]);

  // === Custom checkbox option ===
  const Option = (props) => (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        style={{ marginRight: 8 }}
      />
      {props.label}
    </components.Option>
  );

  // === Add "Select All" in dropdown ===
  const MenuList = (props) => {
    const { options, getValue, setValue } = props;
    const selectedValues = getValue();
    const allSelected =
      options.length > 0 &&
      options.every((opt) =>
        selectedValues.some((sel) => sel.value === opt.value)
      );

    const handleToggleSelectAll = () => {
      if (allSelected) setValue([]);
      else setValue(options);
    };

    return (
      <components.MenuList {...props}>
        <div
          style={{
            padding: "8px 12px",
            borderBottom: "1px solid #ddd",
            cursor: "pointer",
            background: "#f7fafc",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
          onClick={handleToggleSelectAll}
        >
          <input type="checkbox" checked={allSelected} readOnly />
          <strong>{allSelected ? "Unselect All" : "Select All"}</strong>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  // === KEEP YOUR ORIGINAL handleExport EXACTLY ===
  const handleExport = async () => {
    const allSelected = Object.values(selected).flat();
    if (!allSelected.length) {
      toast({
        title: "No selection",
        description: "Please select at least one configuration to export.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setExporting(true);
      const response = await axios.post(
        `/api/exportConfig`,
        { ids: allSelected.map((s) => s.value) },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/zip",
          },
          responseType: "arraybuffer",
        }
      );

      // === unchanged download logic ===
      const disposition = response.headers["content-disposition"];
      const filename =
        disposition?.match(/filename="?(.+)"?/)?.[1] || "exported-config.zip";
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: `${allSelected.length} configuration(s) exported.`,
        status: "success",
        duration: 3000,
      });

      // Reset selections
      const reset = {};
      CONFIG_TYPES.forEach((t) => (reset[t] = []));
      setSelected(reset);
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: "Export Failed",
        description: error.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box mx="auto">
      <VStack align="stretch" spacing={6}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="xl" fontWeight="semibold">
            Export Configuration Files
          </Text>
          <Button
            variant="primary"
            onClick={handleExport}
            isLoading={exporting}
            isDisabled={
              exporting ||
              !Object.values(selected).some((arr) => arr && arr.length > 0)
            }
            _hover={{ bg: "#3D1413" }}
            width="250px"
          >
            Export Selected
          </Button>
        </Flex>

        {loading ? (
          <Spinner alignSelf="center" size="lg" />
        ) : (
          <SimpleGrid columns={4} spacing={4}>
            {CONFIG_TYPES.map((type) => {
              const selectedCount = selected[type]?.length || 0;
              const totalCount = options[type]?.length || 0;
              return (
                <Box key={type}>
                  <Flex alignItems="center" gap={4}>
                    <Text fontWeight="medium" mb={2}>
                      {type}
                    </Text>
                    <Text as="span" color="gray.500" fontSize="sm">
                      ({selectedCount} selected / {totalCount} total)
                    </Text>
                  </Flex>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    options={options[type] || []}
                    value={selected[type] || []}
                    onChange={(val) =>
                      setSelected((prev) => ({ ...prev, [type]: val }))
                    }
                    placeholder={`Select ${type} configuration(s)...`}
                    components={{ Option, MenuList }}
                    styles={customStyles}
                  />
                </Box>
              );
            })}
          </SimpleGrid>
        )}
      </VStack>
    </Box>
  );
}
const customStyles = {
  control: (base) => ({
    ...base,
    borderColor: "#CBD5E0",
    boxShadow: "none",
    minHeight: "38px",
    maxHeight: "90px",
    overflowY: "auto", // scroll inside the selected chips area
  }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: 80,
    overflowY: "auto",
    scrollbarWidth: "thin",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 250, // dropdown list scroll limit
    overflowY: "auto",
  }),
};
